// © Microsoft Corporation. All rights reserved.

import { Telemetry, TelemetryActionType, TelemetryBehaviorType, TelemetryContentType, TelemetryContentViewType, TelemetryObjectBase } from "@msnews/telemetry-contracts";
import { TelemetryBase, appEnvironment, getAppEnvironment } from "@msnews/core";

import { PageType } from "@msnews/experiences-constants";
import ReactDOM from "react-dom";
import { enums } from "./enums";
import { logger } from "@msnews/diagnostics";

interface PrimeCustomTelemetryHandlers {
    handleTelemetryCall: Function;
    handlePartialPageImpressionCall: Function;
    handleUpdateTemplValueCallback?: Promise<Function>;
}

export class SocialTelemetryManager {
    private static primeHandlers: PrimeCustomTelemetryHandlers;
    private static dispatchDedupe = {};

    public static setPrimeHandlers(handlers: PrimeCustomTelemetryHandlers) {
        if (!this.primeHandlers) {
            this.primeHandlers = handlers;
        } else {
            this.primeHandlers = {
                ...this.primeHandlers,
                ...handlers
            };
        }
    }

    public static dispatchDedupedTelemetryEvent(telemetryEvent, element) {
        const now = Date.now(); // current timestamp in ms.

        if (this.dispatchDedupe[telemetryEvent.eventName]) {
            if (this.dispatchDedupe[telemetryEvent.eventName] > 0 && now - this.dispatchDedupe[telemetryEvent.eventName] <= 500) {
                logger.log("Event deduped");
                return;
            }
        }
        this.dispatchDedupe[telemetryEvent.eventName] = now;
        this.handleEvent(telemetryEvent, element, this.primeHandlers && this.primeHandlers.handleTelemetryCall, this.primeHandlers && this.primeHandlers.handlePartialPageImpressionCall);
    }

    public static setTemplValue(key, value) {
        if (Telemetry?.addOrUpdateTmplProperty) {
            Telemetry.addOrUpdateTmplProperty(key, value);
        } else if (this.primeHandlers?.handleUpdateTemplValueCallback) {
            this.primeHandlers.handleUpdateTemplValueCallback.then(updateTemplValue => {
                updateTemplValue(`${key}:${value}`);
            });
        }
    }

    private static handleEvent(telemetryEvent, element, customClickHandler?, customImpressionHandler?) {
        try {
            const action = (telemetryEvent.subTypes && telemetryEvent.subTypes.action) || (telemetryEvent.contract && telemetryEvent.contract.action) || TelemetryActionType.Click;
            const behavior = (telemetryEvent.subTypes && telemetryEvent.subTypes.behavior) || (telemetryEvent.contract && telemetryEvent.contract.behavior) || TelemetryBehaviorType.Undefined;
            switch (telemetryEvent.eventType) {
                case UpperTelemetryType.ContentView:
                    if (customImpressionHandler) {
                        customImpressionHandler(element, telemetryEvent.subTypes.viewType);
                        break;
                    }

                    if (telemetryEvent.sendPeregrine) {
                        Telemetry.sendContentViewEvent(element, telemetryEvent.subTypes.viewType); // viewType: TelemetryContentViewType (Load, Partial etc.)
                    }
                    break;
                default:
                    if (customClickHandler) {
                        const headline = (telemetryEvent.contract && telemetryEvent.contract.content && telemetryEvent.contract.content.headline) || `UnknownTelemetryEventName:${telemetryEvent.eventName}`;
                        const isNavigate = behavior == TelemetryBehaviorType.Navigate;
                        const primeEventType = (telemetryEvent.subTypes && telemetryEvent.subTypes.action) || (telemetryEvent.contract && telemetryEvent.contract.action) || "click_nonnav";
                        const destination = (telemetryEvent.contract && telemetryEvent.contract.destinationUrl) || null;

                        customClickHandler(
                            element,
                            action,
                            behavior,
                            { type: isNavigate ? "click" : primeEventType, originalEvent: "click", target: element },
                            destination,
                            headline
                        );
                        break;
                    }
                    if (telemetryEvent.sendPeregrine == false) { break; }
                    element.setAttribute("data-customhandled", true);
                    Telemetry.sendActionEvent(element, action, behavior);
                    break;
            }
        } catch (e) {
            logger.logError(e);
        }
    }
}

/** PUBLIC TELEMETRY UTILS */
/**
 * @param elementTelemetryObject Telemetry object reference to generate tags from
 * @param isVisible Flag that should set to true if impressions should be sent for given telemetry object
 * @return Telemetry data tags for prime or peregrine
 */
export function renderHybrid(elementTelemetryObject: TelemetryObjectBase, isVisible: boolean, refCallback?: (HTMLElement) => void) {
    if (!elementTelemetryObject) {
        return {};
    }

    // Attach content category and content vertical information to contract if applicable
    if (isArticlePage() || isGalleryPage() || isVideoPage()) {
        elementTelemetryObject.contract.content = {...elementTelemetryObject.contract.content, ...getPageContentCategoryAndVertical()};
    }

    // When Hybrid mode active
    const customTelemetryTags = convertToHybridTags(elementTelemetryObject);
    const finalAttributes = {ref: (elementRef: HTMLElement) => {
        const element = ReactDOM.findDOMNode(elementRef);
        try {
            if (refCallback) {
                refCallback(element);
            }
        } catch (e) {
            logger.logError(e);
        } finally {
            if (customTelemetryTags && element && !element.getAttribute("data-id")) {
                TelemetryBase.setTrackingAttrs(element, element.parentElement, {defaults: customTelemetryTags}, !isVisible);
            }
        }
    }};

    if (!customTelemetryTags) {
        /** Generate Tags from telemetry object */
        const telemetryTagsObject = elementTelemetryObject.render();
        finalAttributes["data-t"] = telemetryTagsObject["data-t"];
    }
    return finalAttributes;
}

/**
 * @param parentTelemetryObject Telemetry object reference of parent element/context
 * @param telemetryContract The telemetry contract that is used when creating new telemetry object
 * @param dynamicContentObject Object passed to set/override dynamic fields of content if there are any e.g: id, headline
 * @param existingObject Previously created telemetry object if its wanted to be updated
 * @return Created or updated telemetry object
 */
export function addOrUpdateTelemetryObject(parentTelemetryObject, telemetryContract, dynamicContentObject, existingObject?): TelemetryObjectBase {
    const uniqueTelemetryContract = {...telemetryContract, content: {...telemetryContract.content, ...(dynamicContentObject || {})}};
    return parentTelemetryObject ? parentTelemetryObject.addOrUpdateChild(uniqueTelemetryContract, existingObject || null) : null;
}

/** Telementry Event Dispatcher: used in components */
export function dispatchTelemetryEvent(telemetryEvent, element, telemetryAttributes = null) {
    try {
        if (telemetryAttributes && telemetryAttributes.content) {
            telemetryEvent.contract.content = {...telemetryEvent.contract.content, ...telemetryAttributes.content};
        }
        SocialTelemetryManager.dispatchDedupedTelemetryEvent(telemetryEvent, element);
    } catch (e) {
        // Suppress the error
    }
}

export function genericTelemetryClickDispatcher(element: any, telemetryObject: any): void {
    const telemetryEventDispatcherInfo = {
        eventName: TelemetryEventNames.genericAction,
        contract: {...(telemetryObject && telemetryObject.contract)}
    };

    dispatchTelemetryEvent(telemetryEventDispatcherInfo, element);
}

export function getHeadlineByReactionType(reactionType, isUndo?) {
    return `${isUndo ? "Undo " : ""}${reactionType[0].toUpperCase()}${reactionType.slice(1)}`;
}

export function getPageContentType() {
    if (isGalleryPage()) {
        return TelemetryContentType.Gallery;
    } else if (isArticlePage()) {
        return TelemetryContentType.Article;
    } else if (isVideoPage()) {
        return TelemetryContentType.Video;
    } else {
        return TelemetryContentType.Article;
    }
}

/** PRIVATE TELEMETRY UTILS */
/**
 * @param elementTelemetryObject Telemetry object reference to generate tags from
 * @return Telemetry data tags for prime
 */
function convertToHybridTags(elementTelemetryObject: TelemetryObjectBase): any {
    if (!elementTelemetryObject || !TelemetryBase.setTrackingAttrs) {
        return null;
    }

    // Generate Tags for Hybrid pages
    const contract: any = elementTelemetryObject.contract || {};
    const content = contract.content || {};
    const customTelemetryTags = {
        n: contract.name,
        l: content.id,
        hl: content.headline,
        ct: content.type,
        c: content.category,
        v: content.vertical,
        b: contract.behavior
    };

    // Remove undefined keys
    Object.keys(customTelemetryTags).map(key => {
        if (!customTelemetryTags[key]) {
            delete customTelemetryTags[key];
        }
    });
    return customTelemetryTags;
}

function isGalleryPage() {
    const pageType = appEnvironment.CurrentRequestTargetScope && appEnvironment.CurrentRequestTargetScope.pageType as string;
    return pageType && (
        pageType === PageType.Gallery || pageType === PageType.GalleryFullScreen || pageType === PageType.ArticleFullScreen || pageType.startsWith("gallery")
    );
}

function isArticlePage() {
    const pageType = appEnvironment.CurrentRequestTargetScope && appEnvironment.CurrentRequestTargetScope.pageType as string;
    return pageType && pageType !== PageType.ArticleFullScreen && (
        pageType === PageType.Article || pageType.startsWith("article")
    );
}

function isVideoPage() {
    const pageType = appEnvironment.CurrentRequestTargetScope && appEnvironment.CurrentRequestTargetScope.pageType as string;
    return pageType &&  (
        pageType === PageType.Video || pageType === "channelplayerpage" || pageType.startsWith("video")
    );
}

function getPageContentCategoryAndVertical() {
    return {
        category: appEnvironment.HostPage && appEnvironment.HostPage.categoryKey,
        vertical: appEnvironment.HostPage && appEnvironment.HostPage.verticalKey
    };
}

/** TELEMETRY TYPES */
export const UpperTelemetryType = {
    ContentView: "ContentView",
    Action: "Action"
};

export const TelemetryEventNames = {
    // event names used at component level
    genericLoaded: "generic-loaded",
    genericAction: "generic-action",
    genericHover: "generic-hover"

};

/** Social Event Types */
export const TelemetryEventMap = {
    // events used at component level
    genericLoaded: {
        eventName: TelemetryEventNames.genericLoaded,
        eventType: UpperTelemetryType.ContentView,
        contract: {},
        subTypes: {viewType: TelemetryContentViewType.Partial},
        sendPrime: true,
        sendPeregrine: false
    },
    genericHover: {
        eventName: TelemetryEventNames.genericHover,
        subTypes: {action: TelemetryActionType.Hover}
    }
};

export const ReactionToBehaviorTypeMap = {
    [enums.reactions.angry]: TelemetryBehaviorType.Angry,
    [enums.reactions.like]: TelemetryBehaviorType.Like,
    [enums.reactions.love]: TelemetryBehaviorType.Love,
    [enums.reactions.sad]: TelemetryBehaviorType.Sad,
    [enums.reactions.surprised]: TelemetryBehaviorType.Surprised,
    [enums.reactions.thinking]: TelemetryBehaviorType.Thinking
};